// src/store/slices/pharmacyPurchaseSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import BaseUrl from '../../../BaseUrl';

const API_BASE_URL = BaseUrl.pharmacyurl

export const fetchPurchases = createAsyncThunk(
  'pharmacyPurchase/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`${API_BASE_URL}PharmacyPurchases`, {
        headers: { 
          Authorization: `Bearer ${token}` 
        }
      });
      console.log(response.data)
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createPurchase = createAsyncThunk(
  'pharmacyPurchase/create',
  async (purchaseData, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('accessToken');
      const apiData = {
        clientId: purchaseData.clientId,
        branchId: purchaseData.branchId,
        medicineId: purchaseData.medicineId,
        numberOfUnits: purchaseData.numberOfUnits,
        unitCost: purchaseData.unitCost,
        unitSellingPrice: purchaseData.unitSellingPrice,
        storePrice: purchaseData.storePrice,
        tax: purchaseData.tax,
        expiryDate: purchaseData.expiryDate,
        purchaseDate: purchaseData.purchaseDate,
        reorderLevel: purchaseData.reorderLevel,
        barcode: purchaseData.barcode,
        description: purchaseData.description,
        category: purchaseData.category,  // This is now an integer enum value
        itemName: purchaseData.itemName
      };
      console.log('Creating purchase:', JSON.stringify(apiData, null, 2));
      const response = await axios.post(`${API_BASE_URL}PharmacyPurchases`, apiData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.log(error)
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updatePurchase = createAsyncThunk(
  'pharmacyPurchase/update',
  async ({ id, purchaseData }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('accessToken');
      const apiData = {
        id: id,
        clientId: purchaseData.clientId,
        branchId: purchaseData.branchId,
        medicineId: purchaseData.medicineId,
        numberOfUnits: purchaseData.numberOfUnits,
        unitCost: purchaseData.unitCost,
        unitSellingPrice: purchaseData.unitSellingPrice,
        storePrice: purchaseData.storePrice,
        tax: purchaseData.tax,
        expiryDate: purchaseData.expiryDate,
        purchaseDate: purchaseData.purchaseDate,
        reorderLevel: purchaseData.reorderLevel,
        barcode: purchaseData.barcode,
        description: purchaseData.description,
        category: purchaseData.category,  // This is now an integer enum value
        itemName: purchaseData.itemName
      };
      
      console.log('Updating purchase:', JSON.stringify(apiData, null, 2));
      
      const response = await axios.put(`${API_BASE_URL}PharmacyPurchases/${id}`, apiData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Update error:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deletePurchase = createAsyncThunk(
  'pharmacyPurchase/delete',
  async (id, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('accessToken');
      await axios.delete(`${API_BASE_URL}PharmacyPurchases/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const pharmacyPurchaseSlice = createSlice({
  name: 'pharmacyPurchase',
  initialState: {
    purchases: [],
    loading: false,
    error: null,
    totalSpent: 0,
    lowStockItems: []
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    calculateStats: (state) => {
      state.totalSpent = state.purchases.reduce((sum, purchase) => 
        sum + ((purchase.numberOfUnits || 0) * (purchase.unitCost || 0)), 0
      );
      state.lowStockItems = state.purchases.filter(purchase => {
        const currentStock = purchase.numberOfUnits || 0;
        const reorderLevel = purchase.reorderLevel || 0;
        return reorderLevel > 0 && currentStock <= reorderLevel;
      });
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch All
      .addCase(fetchPurchases.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPurchases.fulfilled, (state, action) => {
        state.loading = false;
        state.purchases = action.payload.map(purchase => ({
          ...purchase,
          numberOfUnits: purchase.numberOfUnits,
          unitCost: purchase.unitCost,
          unitSellingPrice: purchase.unitSellingPrice,
          category: purchase.category  // Keep the integer enum value
        }));
        
        state.totalSpent = state.purchases.reduce((sum, purchase) => 
          sum + ((purchase.numberOfUnits || 0) * (purchase.unitCost || 0)), 0
        );
        
        state.lowStockItems = state.purchases.filter(purchase => {
          const currentStock = purchase.numberOfUnits || 0;
          const reorderLevel = purchase.reorderLevel || 0;
          return reorderLevel > 0 && currentStock <= reorderLevel;
        });
      })
      .addCase(fetchPurchases.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Create
      .addCase(createPurchase.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createPurchase.fulfilled, (state, action) => {
        state.loading = false;
        state.purchases.unshift(action.payload);
        
        state.totalSpent = state.purchases.reduce((sum, purchase) => 
          sum + ((purchase.numberOfUnits || 0) * (purchase.unitCost || 0)), 0
        );
        
        state.lowStockItems = state.purchases.filter(purchase => {
          const currentStock = purchase.numberOfUnits || 0;
          const reorderLevel = purchase.reorderLevel || 0;
          return reorderLevel > 0 && currentStock <= reorderLevel;
        });
      })
      .addCase(createPurchase.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Update
      .addCase(updatePurchase.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updatePurchase.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.purchases.findIndex(p => p.id === action.payload.id);
        if (index !== -1) {
          state.purchases[index] = action.payload;
          
          state.totalSpent = state.purchases.reduce((sum, purchase) => 
            sum + ((purchase.numberOfUnits || 0) * (purchase.unitCost || 0)), 0
          );
          
          state.lowStockItems = state.purchases.filter(purchase => {
            const currentStock = purchase.numberOfUnits || 0;
            const reorderLevel = purchase.reorderLevel || 0;
            return reorderLevel > 0 && currentStock <= reorderLevel;
          });
        }
      })
      .addCase(updatePurchase.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Delete
      .addCase(deletePurchase.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deletePurchase.fulfilled, (state, action) => {
        state.loading = false;
        state.purchases = state.purchases.filter(p => p.id !== action.payload);
        
        state.totalSpent = state.purchases.reduce((sum, purchase) => 
          sum + ((purchase.numberOfUnits || 0) * (purchase.unitCost || 0)), 0
        );
        
        state.lowStockItems = state.purchases.filter(purchase => {
          const currentStock = purchase.numberOfUnits || 0;
          const reorderLevel = purchase.reorderLevel || 0;
          return reorderLevel > 0 && currentStock <= reorderLevel;
        });
      })
      .addCase(deletePurchase.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export const { clearError, calculateStats } = pharmacyPurchaseSlice.actions;
export default pharmacyPurchaseSlice.reducer;